import { COMPTES_DEMO, JOURNAL_DEMO } from '@/shared/demo/donnees-demo';
import { aujourdhui } from '@/shared/referentiel/conformite';
import { DEPARTEMENTS } from '@/shared/referentiel/departements';
import { graineChantiers } from './graine-chantiers';
import type { Position, Site } from '@/shared/donnees/types';
import type { EtatDemo } from './magasin';

/** « AAAA-MM-JJ » décalé de `jours` depuis aujourd'hui : les échéances restent vraies au fil des jours. */
function jour(jours: number): string {
  const [a, m, j] = aujourdhui().split('-').map(Number);
  return new Date(Date.UTC(a ?? 2026, (m ?? 1) - 1, (j ?? 1) + jours)).toISOString().slice(0, 10);
}

function ilYa(joursEnArriere: number): string {
  return new Date(Date.now() - joursEnArriere * 86_400_000).toISOString();
}

function fichier(id: string, nom: string, dossier: string, ajoute: number) {
  const proprietaire = dossier.split('/')[1] ?? '';
  return {
    entrepriseId: proprietaire,
    partenaireId: proprietaire,
    id,
    nom,
    taille: 180_000 + nom.length * 1000,
    typeMime: nom.endsWith('.pdf') ? 'application/pdf' : 'image/jpeg',
    chemin: `${dossier}/${id}-${nom}`,
    ajouteLe: ilYa(ajoute),
    ajoutePar: 'u-bureau',
  };
}

/** Les positions des sites du jeu de départ, telles que le géocodeur les rendrait. */
export const POSITIONS_DEPART: Record<string, Position> = {
  's-hangar-nord': { latitude: 43.9883, longitude: 2.4127, precision: 'commune' },
  's-borie': { latitude: 43.9786, longitude: 2.3958, precision: 'commune' },
  's-marre': { latitude: 44.1613, longitude: 1.5371, precision: 'adresse' },
  's-remparts': { latitude: -21.3677, longitude: 55.6239, precision: 'adresse' },
  's-lesponne': { latitude: 42.9958, longitude: 0.1197, precision: 'commune' },
};

const OCCITANIE = DEPARTEMENTS.filter((d) => d.region === 'Occitanie').map((d) => d.code);

/** Le jeu de départ : les personnages des maquettes (docs/maquettes/). */
export function graine(): EtatDemo {
  const vide = { adresse: '', codePostal: '', commune: '', notes: '' };
  return {
    version: 6,
    ...graineChantiers(),
    entreprises: [
      {
        id: 'e-bousquet',
        raisonSociale: 'GAEC du Bousquet du Cambon',
        forme: 'GAEC',
        siret: '498 765 432 00018',
        activite: 'Élevage ovin, fourrage',
        adresse: 'Le Bousquet',
        codePostal: '81190',
        commune: 'Calmels-et-le-Viala',
        notes: '',
        creeLe: ilYa(8),
      },
      {
        id: 'e-marre',
        raisonSociale: 'Exploitation Marre',
        forme: 'EARL',
        siret: '512 004 118 00027',
        activite: 'Noix, prunes',
        adresse: '1250 route de Montauban',
        codePostal: '82300',
        commune: 'Caussade',
        notes: 'Accès poids lourds par le chemin de la Brugue.',
        creeLe: ilYa(6),
      },
      {
        id: 'e-saint-joseph',
        raisonSociale: 'Scierie de Saint-Joseph',
        forme: 'SAS',
        siret: '834 221 907 00012',
        activite: 'Sciage, bois de charpente',
        adresse: '12 chemin de la Rivière des Remparts',
        codePostal: '97480',
        commune: 'Saint-Joseph',
        notes: '',
        creeLe: ilYa(20),
      },
      {
        id: 'e-pujo',
        raisonSociale: 'Exploitation Pujo',
        forme: 'Exploitation individuelle',
        siret: '',
        activite: 'Foin de montagne',
        adresse: 'Quartier Lesponne',
        codePostal: '65710',
        commune: 'Campan',
        notes: '',
        creeLe: ilYa(3),
      },
    ],
    sites: (
      [
        {
          id: 's-hangar-nord',
          entrepriseId: 'e-bousquet',
          nom: 'Hangar nord',
          adresse: 'Le Bousquet',
          codePostal: '81190',
          commune: 'Calmels-et-le-Viala',
          departement: '81',
          notes: '',
        },
        {
          id: 's-borie',
          entrepriseId: 'e-bousquet',
          nom: 'Séchoir à fourrage',
          adresse: 'La Borie',
          codePostal: '81190',
          commune: 'Calmels-et-le-Viala',
          departement: '81',
          notes: 'Bâtiment à construire.',
        },
        {
          id: 's-marre',
          entrepriseId: 'e-marre',
          nom: 'Hangar à noix',
          adresse: '1250 route de Montauban',
          codePostal: '82300',
          commune: 'Caussade',
          departement: '82',
          notes: '',
        },
        {
          id: 's-remparts',
          entrepriseId: 'e-saint-joseph',
          nom: 'Parc à bois',
          adresse: '12 chemin de la Rivière des Remparts',
          codePostal: '97480',
          commune: 'Saint-Joseph',
          departement: '974',
          notes: 'Livraison par conteneur depuis Le Port.',
        },
        {
          id: 's-lesponne',
          entrepriseId: 'e-pujo',
          nom: 'Grange de Lesponne',
          adresse: 'Quartier Lesponne',
          codePostal: '65710',
          commune: 'Campan',
          departement: '65',
          notes: '',
        },
      ] satisfies Omit<Site, 'position'>[]
    ).map((s) => ({ ...s, position: POSITIONS_DEPART[s.id] ?? null })),
    contacts: [
      {
        id: 'c-jcambon',
        entrepriseId: 'e-bousquet',
        prenom: '',
        nom: 'J. Cambon',
        fonction: 'Gérant',
        telephone: '06 12 34 56 78',
        email: 'j.cambon@gaec-bousquet.demo',
        surPlace: true,
      },
      {
        id: 'c-acambon',
        entrepriseId: 'e-bousquet',
        prenom: '',
        nom: 'A. Cambon',
        fonction: 'Administratif',
        telephone: '05 63 00 00 01',
        email: 'a.cambon@gaec-bousquet.demo',
        surPlace: false,
      },
      {
        id: 'c-marre',
        entrepriseId: 'e-marre',
        prenom: '',
        nom: 'Hélène Marre',
        fonction: 'Exploitante',
        telephone: '06 70 11 22 33',
        email: 'helene@marre.demo',
        surPlace: true,
      },
      {
        id: 'c-hoarau',
        entrepriseId: 'e-saint-joseph',
        prenom: '',
        nom: 'Didier Hoarau',
        fonction: 'Chef de site',
        telephone: '0692 45 67 89',
        email: 'd.hoarau@scierie-sj.demo',
        surPlace: true,
      },
    ],
    documentsEntreprise: [
      {
        ...fichier(
          'de-devis-bousquet',
          'Devis signé — séchoir 180 m².pdf',
          'entreprises/e-bousquet',
          8
        ),
        categorie: 'devis-signe',
      },
      {
        ...fichier('de-kbis-bousquet', 'Kbis GAEC.pdf', 'entreprises/e-bousquet', 8),
        categorie: 'administratif',
      },
      {
        ...fichier('de-devis-marre', 'Devis signé Marre.pdf', 'entreprises/e-marre', 6),
        categorie: 'devis-signe',
      },
      {
        ...fichier('de-devis-sj', 'Devis signé Saint-Joseph.pdf', 'entreprises/e-saint-joseph', 20),
        categorie: 'devis-signe',
      },
    ],
    partenaires: [
      {
        id: 'p-batisud',
        raisonSociale: 'Bâti Sud Charpente',
        type: 'sous-traitant',
        siret: '812 345 678 00021',
        contactPrenom: '',
        contactNom: 'P. Garrigues',
        telephone: '05 63 00 00 00',
        email: 'contact@batisud.demo',
        adresse: '4 avenue de l’Europe',
        codePostal: '82000',
        commune: 'Montauban',
        etapes: ['batiment', 'reception'],
        zones: ['12', '31', '81', '82'],
        notes: '',
        creeLe: ilYa(30),
      },
      {
        id: 'p-solaire-aveyron',
        raisonSociale: 'Solaire Aveyron Pose',
        type: 'sous-traitant',
        siret: '790 112 334 00015',
        contactPrenom: '',
        contactNom: 'M. Fabre',
        telephone: '05 65 00 00 00',
        email: 'pose@solaire-aveyron.demo',
        ...vide,
        codePostal: '12000',
        commune: 'Rodez',
        etapes: ['solaire'],
        zones: ['12', '81', '82'],
        creeLe: ilYa(30),
      },
      {
        id: 'p-aero-sechage',
        raisonSociale: 'Aéro Séchage Services',
        type: 'sous-traitant',
        siret: '881 450 002 00019',
        contactPrenom: '',
        contactNom: 'L. Mercadier',
        telephone: '05 61 00 00 00',
        email: 'atelier@aero-sechage.demo',
        ...vide,
        codePostal: '31000',
        commune: 'Toulouse',
        etapes: ['sechage'],
        zones: OCCITANIE,
        creeLe: ilYa(25),
      },
      {
        id: 'p-energie-pei',
        raisonSociale: 'Énergie Péi Réunion',
        type: 'sous-traitant',
        siret: '851 220 774 00031',
        contactPrenom: '',
        contactNom: 'S. Payet',
        telephone: '0262 00 00 00',
        email: 'contact@energie-pei.demo',
        ...vide,
        codePostal: '97410',
        commune: 'Saint-Pierre',
        etapes: ['solaire', 'sechage', 'reception'],
        zones: ['974'],
        creeLe: ilYa(26),
      },
      {
        id: 'p-riviere',
        raisonSociale: 'Transports Rivière',
        type: 'sous-traitant',
        siret: '',
        contactPrenom: '',
        contactNom: 'L. Rivière',
        telephone: '05 63 00 11 11',
        email: 'exploitation@transports-riviere.demo',
        ...vide,
        codePostal: '81000',
        commune: 'Albi',
        etapes: ['livraison'],
        zones: ['09', '11', '12', '31', '32', '46', '65', '81', '82'],
        creeLe: ilYa(12),
      },
    ],
    conformite: [
      {
        ...fichier('dc-bs-kbis', 'Kbis Bâti Sud.pdf', 'conformite/p-batisud', 20),
        type: 'kbis',
        expireLe: jour(70),
      },
      {
        ...fichier('dc-bs-dec', 'Décennale 2026.pdf', 'conformite/p-batisud', 20),
        type: 'decennale',
        expireLe: jour(92),
      },
      {
        ...fichier('dc-bs-urssaf', 'Vigilance URSSAF.pdf', 'conformite/p-batisud', 170),
        type: 'urssaf',
        expireLe: jour(11),
      },
      {
        ...fichier('dc-sa-kbis', 'Kbis.pdf', 'conformite/p-solaire-aveyron', 15),
        type: 'kbis',
        expireLe: jour(75),
      },
      {
        ...fichier('dc-sa-dec', 'Décennale.pdf', 'conformite/p-solaire-aveyron', 15),
        type: 'decennale',
        expireLe: jour(200),
      },
      {
        ...fichier('dc-sa-urssaf', 'URSSAF.pdf', 'conformite/p-solaire-aveyron', 15),
        type: 'urssaf',
        expireLe: jour(165),
      },
      {
        ...fichier('dc-sa-rge', 'RGE QualiPV.pdf', 'conformite/p-solaire-aveyron', 15),
        type: 'qualification',
        expireLe: jour(300),
      },
      {
        ...fichier('dc-as-kbis', 'Kbis.pdf', 'conformite/p-aero-sechage', 40),
        type: 'kbis',
        expireLe: jour(50),
      },
      {
        ...fichier('dc-as-dec', 'Décennale.pdf', 'conformite/p-aero-sechage', 40),
        type: 'decennale',
        expireLe: jour(140),
      },
      {
        ...fichier('dc-as-urssaf', 'URSSAF.pdf', 'conformite/p-aero-sechage', 190),
        type: 'urssaf',
        expireLe: jour(-4),
      },
      {
        ...fichier('dc-ep-kbis', 'Kbis.pdf', 'conformite/p-energie-pei', 10),
        type: 'kbis',
        expireLe: jour(80),
      },
      {
        ...fichier('dc-ep-dec', 'Décennale.pdf', 'conformite/p-energie-pei', 10),
        type: 'decennale',
        expireLe: jour(250),
      },
      {
        ...fichier('dc-ep-urssaf', 'URSSAF.pdf', 'conformite/p-energie-pei', 10),
        type: 'urssaf',
        expireLe: jour(170),
      },
      {
        ...fichier('dc-tr-dec', 'Décennale.pdf', 'conformite/p-riviere', 12),
        type: 'decennale',
        expireLe: jour(120),
      },
      {
        ...fichier('dc-tr-urssaf', 'URSSAF.pdf', 'conformite/p-riviere', 12),
        type: 'urssaf',
        expireLe: jour(160),
      },
    ],
    comptes: COMPTES_DEMO.map((c) => ({ ...c })),
    journal: JOURNAL_DEMO.map((e) => ({
      ...e,
      changements: e.changements as Record<string, [unknown, unknown]>,
    })),
  };
}
